import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShoppingCart, ArrowLeft, Check } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { formatVariant } from '../lib/formatVariant';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ProductDetail = ({ onAddToCart }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [loading, setLoading] = useState(true);

  const selectedSize = selectedVariant ? formatVariant(selectedVariant) : null;
  const selectedPrice = selectedVariant?.price || 0;

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API}/products/${id}`);
        const productData = response.data;
        setProduct(productData);
        
        // Set default variant
        if (productData.variants && productData.variants.length > 0) {
          setSelectedVariant(productData.variants[0]);
        }
      } catch (error) {
        console.error('Error fetching product:', error);
        toast.error('Product not found');
        navigate('/products');
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id, navigate]);

  const handleAddToCart = () => {
    if (product) {
      const productToAdd = {
        ...product,
        price: selectedPrice,
        selectedSize: selectedSize,
        displayName: selectedSize ? `${product.name} (${selectedSize})` : product.name
      };

      for (let i = 0; i < quantity; i++) {
        onAddToCart(productToAdd);
      }
      toast.success(`${quantity} ${productToAdd.displayName}(s) added to cart`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-32 flex items-center justify-center" data-testid="product-detail-loading">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  if (!product) return null;

  return (
    <div data-testid="product-detail-page" className="min-h-screen pt-32 pb-24">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <button
          onClick={() => navigate(-1)}
          data-testid="back-button"
          className="flex items-center space-x-2 text-muted-foreground hover:text-primary transition-colors duration-300 mb-12"
        >
          <ArrowLeft size={20} strokeWidth={1.5} />
          <span className="text-sm uppercase tracking-widest">Back</span>
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="space-y-4"
          >
            <div className="aspect-square overflow-hidden border border-white/5">
              <img
                src={product.image}
                alt={product.name}
                className="w-full h-full object-cover"
              />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="space-y-8"
          >
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-primary mb-3 font-medium">
                {product.category}
              </p>
              <h1 className="font-display text-4xl md:text-5xl font-normal tracking-tight text-foreground mb-4">
                {product.name}
              </h1>
              <p className="font-accent text-4xl italic text-primary">
                ₹{selectedPrice.toFixed(2)}
              </p>
            </div>

            {product.variants && product.variants.length > 0 && (
              <div className="border-t border-white/10 pt-8">
                <h2 className="text-sm uppercase tracking-widest font-medium mb-4">Select Size</h2>
                <div className="flex flex-wrap gap-3">
                  {product.variants.map((variant, idx) => {
                    const label = formatVariant(variant);
                    const active = selectedVariant && formatVariant(selectedVariant) === label;
                    return (
                      <button
                        key={`${variant.size}-${variant.unit || ''}-${idx}`}
                        onClick={() => setSelectedVariant(variant)}
                        data-testid={`size-option-${label.replace(/\s/g, '')}`}
                        className={`px-6 py-3 border transition-all duration-300 ${
                          active
                            ? 'bg-primary text-primary-foreground border-primary'
                            : 'border-white/20 hover:border-primary text-foreground'
                        }`}
                      >
                        <div className="text-sm font-medium tracking-wider">{label}</div>
                        <div className="text-xs mt-1">₹{variant.price}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="border-t border-white/10 pt-8">
              <h2 className="text-sm uppercase tracking-widest font-medium mb-4">Description</h2>
              <p className="text-base font-light leading-relaxed text-muted-foreground">
                {product.description}
              </p>
            </div>

            <div className="border-t border-white/10 pt-8 space-y-6">
              <div>
                <label className="text-sm uppercase tracking-widest font-medium block mb-3">
                  Quantity
                </label>
                <div className="flex items-center space-x-4">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    data-testid="decrease-quantity"
                    className="w-12 h-12 border border-white/20 hover:border-primary transition-colors duration-300 flex items-center justify-center"
                  >
                    -
                  </button>
                  <span className="text-xl font-medium w-12 text-center" data-testid="quantity-display">
                    {quantity}
                  </span>
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    data-testid="increase-quantity"
                    className="w-12 h-12 border border-white/20 hover:border-primary transition-colors duration-300 flex items-center justify-center"
                  >
                    +
                  </button>
                </div>
              </div>

              <button
                onClick={handleAddToCart}
                data-testid="add-to-cart-button"
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8 py-4 uppercase tracking-widest text-sm font-medium transition-all duration-300 hover:scale-105 hover:shadow-[0_0_20px_rgba(212,175,55,0.3)] flex items-center justify-center space-x-3"
              >
                <ShoppingCart size={20} strokeWidth={1.5} />
                <span>Add to Cart</span>
              </button>

              <div className="space-y-3 pt-6">
                <div className="flex items-center space-x-3 text-sm text-muted-foreground">
                  <Check size={16} className="text-primary" strokeWidth={1.5} />
                  <span>100% Natural & Organic</span>
                </div>
                <div className="flex items-center space-x-3 text-sm text-muted-foreground">
                  <Check size={16} className="text-primary" strokeWidth={1.5} />
                  <span>Traditional Distillation</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};